import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import url from 'url';
import { Link } from 'react-router-dom';
import { Badge } from 'reactstrap';
import { editWorkerUser, clearWorkerDetails, getWorkerJobsCompletedAction } from '../../../actions/workers';
import { getDetailedUser } from '../../../actions/users';
import { uploadFile, makeDecisionAfterCheckr, getLanguagesList } from '../../../actions';
import { startSupportConversation } from '../../../actions/conversations';
import { GET_WORKER_DETAILS_SUCCESS } from '../../../actions/actionTypes';
import { UIShowAlert, UIShowConfirm } from '../../../actions/ui';
import defaultUserPhoto from '../../../images/default_user.png';
import WorkerPersonalInfo from './WorkerPersonalInfo';
import WorkerSkills from './WorkerSkills';
import WorkerInterview from './WorkerInterview';
import WorkerJobsList from './WorkerJobsList';
import { appPath } from '../../../constants';
import {
  backgroundStatuses,
  onboardingStatusesWithoutAsessment,
  onboardingStatusesUsingOnboard
} from "../../../constants/general";import { regExpFindUnderscore } from '../../../constants/regExps';
import { modifyObject } from '../../../helpers/modifyObject';
import { visuallyFormatPhoneNumber } from '../../../helpers/general';
import SwitchRow from '../../common/forms/SwitchRow';
import Validator from '../../common/forms/Validator';
import { extractWorkerData } from '../../../helpers/workers';
import BackArrow from '../../common/ui/BackArrow';
import WorkerJobSummary from './WorkerJobSummary';

import WorkerStatus from "./WorkerStatus";

class WorkerDetails extends Validator {
  static propTypes = {
    workerDetails: PropTypes.object.isRequired,
    getDetailedUser: PropTypes.func.isRequired,
    clearWorkerDetails: PropTypes.func.isRequired,
    editWorkerUser: PropTypes.func.isRequired,
    uploadFile: PropTypes.func.isRequired,
    makeDecisionAfterCheckr: PropTypes.func.isRequired,
    getLanguagesList: PropTypes.func.isRequired,
    languagesList: PropTypes.array.isRequired,
    startSupportConversation: PropTypes.func.isRequired
  };

  constructor (props) {
    super(props);

    this.initialState = props.workerDetails;

    this.state = {
      ...this.initialState,
      activeTab: 'profile', // can be 'profile' or 'jobs'
      isLoadingPersonalInfo: false,
      isEditingPersonalInfo: false,
      workerNewPhotoUrl: ''
    };
  }

  validationRules = {
    first_name: { required: true },
    last_name: { required: true },
    email: { email: true },
    phone: { required: true },
    address: { required: true }
  };

  componentDidMount () {
    const { getDetailedUser, getLanguagesList, getWorkerJobsCompletedAction, match: { params: { id } }, history } = this.props;
    window.scrollTo(0, 0);
    if (isNaN(id)) {
      history.push(`/${appPath}/workers`);
    } else {
      getDetailedUser(id, GET_WORKER_DETAILS_SUCCESS).then(() => getWorkerJobsCompletedAction(id))
      getLanguagesList();
      const { query } = url.parse(window.location.href, true);
      if (query && query.period) {
        this.setState({
          activeTab: 'jobs'
        });
      }
    }
  }

  componentWillUnmount () {
    this.props.clearWorkerDetails();
  };

  setUserDetails = user => this.setState({ ...user });

  componentDidUpdate (prevProps, prevState, snapshot) {
    if (!prevProps.workerDetails.id && this.props.workerDetails.id) {
      this.initialState = this.props.workerDetails;
      this.setUserDetails(this.props.workerDetails);
    }
  }

  _startConversation = () =>
    this.props.startSupportConversation(this.initialState.id).then(data => {
      if (data) {
        window.open(`/${appPath}/conversation/${data.conversation_id}`, '_blank', 'height=600, width=500');
      }
    });

  switchTab = e => {
    this.setState({
      activeTab: e.target.id
    });
    if (e.target.id === 'profile') {
      this.props.history.replace(this.props.match.url);
    }
  };

  enableEditingPersonalInfo = () => this.setState({ isEditingPersonalInfo: true });

  setNewWorkerPhoto = workerNewPhotoUrl => this.setState({ workerNewPhotoUrl });

  handleChangePrimaryLanguage = event => {
    const { value } = event.target;
    const langId = parseInt(value, 10);
    const { secondary_language_id } = this.state;
    this.setState(prevState => {
      const secondLangId = langId === secondary_language_id ? null : prevState.secondary_language_id;
      return {
        language_id: langId,
        secondary_language_id: secondLangId
      };
    });
  };

  changeWorkerActivity = () => {
    const is_active = !this.props.workerDetails.is_active;

    this.props.UIShowConfirm(
      'Are you sure you want to change worker activity?',
      () => {
        const { editWorkerUser, getDetailedUser } = this.props;
        const { id } = this.state;
        editWorkerUser(id, { is_active }).then(() => {
          getDetailedUser(id, GET_WORKER_DETAILS_SUCCESS);
        });
      }
    );
  };

  makeDecision = decision => {
    const { makeDecisionAfterCheckr, getDetailedUser } = this.props;
    const { compliance_candidate_id, id } = this.state;
    makeDecisionAfterCheckr(compliance_candidate_id, decision).then(
      () => getDetailedUser(id, GET_WORKER_DETAILS_SUCCESS)
    );
  };

  handleChangeAddress = addressInfo => this.setState({ ...addressInfo });

  cancelEditingPersonalInfo = () => {
    this._resetErrors();

    this.setState({
      ...this.initialState,
      workerNewPhotoUrl: '',
      isEditingPersonalInfo: false
    });
  };

  submitEditingPersonalInfo = () => {
    if (this.validate(this.validationRules)) {
      const { editWorkerUser, getDetailedUser } = this.props;
      const { id } = this.initialState;

      this.setState({ isLoadingPersonalInfo: true });

      const workerData = modifyObject(extractWorkerData(this.state));

      editWorkerUser(id, workerData).then(() => {
        getDetailedUser(id, GET_WORKER_DETAILS_SUCCESS).then(() => {
          this.setState({
            isLoadingPersonalInfo: false,
            isEditingPersonalInfo: false
          });
        });
      });
    }
  };

  handleChange = event => {
    let { name, value } = event.target;

    if (name === 'phone') {
      value = visuallyFormatPhoneNumber(value);
    }

    this.setState({ [name]: value });
  };

  handleSwitch = (name, value = undefined) => this.setState(prevState => {
    let newValue = !prevState[name];

    if (value) {
      if (this.state[name] === value) {
        newValue = null;
      } else {
        newValue = value;
      }
    }

    return { [name]: newValue };
  });

  handleChangePassword = (password, temp_password) =>
    this.props.editWorkerUser(this.initialState.id, {
      password,
      temp_password,
    });

  changeOnboardingStatus = (onboarding_status) => {
    this.props.UIShowConfirm(
      "Are you sure you want to change onboarding status?",
      () => {
        let statusChange = false;
        const { editWorkerUser, getDetailedUser } = this.props;
        const { id } = this.state;
        if (onboarding_status === 'onboard_completed') {
          statusChange = true;
        }
        editWorkerUser(id, { onboarding_status, status_change: statusChange }).then(() => {
          getDetailedUser(id, GET_WORKER_DETAILS_SUCCESS);
        });
      }
    );
  };

  changeBackgroundStatus = (background_status) => {
    this.props.UIShowConfirm(
      "Are you sure you want to change background status?",
      () => {
        const { editWorkerUser, getDetailedUser } = this.props;
        const { id } = this.state;
        editWorkerUser(id, { background_status }).then(() => {
          getDetailedUser(id, GET_WORKER_DETAILS_SUCCESS);
        });
      }
    );
  };

  handlePostComment = async (body) => {
    const {
      postWorkerComment,
      searchWorkerComments,
      workerDetails,
    } = this.props;

    body["commentable_id"] = workerDetails.id;

    await postWorkerComment(body);
    searchWorkerComments(workerDetails.id, 0);
  };

  handleLoadMoreComments = () => {
    const { workerDetails, workerComments, searchWorkerComments } = this.props
    searchWorkerComments(workerDetails.id, workerComments.length, true)
  }

  render () {
    const { uploadFile, match: { url }, languagesList, workerDetails } = this.props;
    const { activeTab, isLoadingPersonalInfo, workerNewPhotoUrl, isEditingPersonalInfo, email, onboarding_status, photo_image_url, first_name, last_name, phone, payroll_employee_id,
      background_status, tax_type } = this.state;

    if (!this.initialState.email) {
      return (
        <div className='col col-12 col-lg-9'>
          <div className='d-flex mt-2'>
            <div className='text-muted'>Fetching user info...</div>
          </div>
        </div>
      );
    }

    return (
      <div className='user-details-wrapper'>
        <div className='user-header-wrapper'>
          <div className='user-main-info'>
            <div className='d-flex align-self-start'>
              <BackArrow
                history={this.props.history}
                location={this.props.location}
                defaultBackPath={`/${appPath}/workers`}
              />
              <div
                className='image-icon'
                style={{ backgroundImage: `url('${photo_image_url || defaultUserPhoto}')` }}
              />
            </div>
            <div>
              <h3 className='user-name'>{first_name} {last_name}</h3>
              <p>Email: {email}</p>
              <p>Phone: {phone}</p>
              { !!tax_type && <p>Tax Type: {tax_type}</p> }
              { payroll_employee_id && <p>Payroll Employee Id: {payroll_employee_id}</p> }
            </div>
          </div>
          <div>
            <WorkerStatus
              title="Onboarding"
              currentStatus={workerDetails.onboarding_status}
              statuses={onboardingStatusesUsingOnboard}
              handleChangeStatus={this.changeOnboardingStatus}
            />

            {/* Assessment Completed */}

            {background_status && (
              <WorkerStatus
                title="Background"
                currentStatus={workerDetails.background_status}
                statuses={backgroundStatuses}
                handleChangeStatus={this.changeBackgroundStatus}
              />
            )}
            { background_status === backgroundStatuses.CONSIDER && (
              <div className='worker-decision'>
                <button
                  className='button button-delete'
                  onClick={() => this.makeDecision(false)}
                >Decline</button>
                <button
                  className='button button-primary ml-3'
                  onClick={() => this.makeDecision(true)}
                >Move Forward</button>
              </div>
            )}

            <SwitchRow checked={workerDetails.is_active || false} onChange={this.changeWorkerActivity} label='Is Active' />
          </div>
        </div>

        <button className='button button-primary mt-3 mt-sm-0' onClick={this._startConversation}>Send Message</button>

        <div className='tabs-wrapper'>
          <button
            id='profile'
            className={activeTab === 'profile' ? 'tab active' : 'tab'}
            onClick={this.switchTab}
          >Profile</button>
          <button
            id='jobs'
            className={activeTab === 'jobs' ? 'tab active' : 'tab'}
            onClick={this.switchTab}
          >Jobs</button>
        </div>
        {activeTab === 'profile' && (
          <main className='main-wrapper-details-blocks'>
            <WorkerPersonalInfo
              {...this.state}
              isEditing={isEditingPersonalInfo}
              enableEditingPersonalInfo={this.enableEditingPersonalInfo}
              handleChange={this.handleChange}
              handleChangeAddress={this.handleChangeAddress}
              handleChangePrimaryLanguage={this.handleChangePrimaryLanguage}
              handleChangePassword={this.handleChangePassword}
              handleSwitch={this.handleSwitch}
              cancelEditingPersonalInfo={this.cancelEditingPersonalInfo}
              savePersonalInfo={this.submitEditingPersonalInfo}
              validationErrors={this.validationErrors}
              uploadFile={uploadFile}
              isLoadingPersonalInfo={isLoadingPersonalInfo}
              userNewPhotoUrl={workerNewPhotoUrl}
              setNewUserPhoto={this.setNewWorkerPhoto}
              languagesList={languagesList}
              worker={workerDetails}
            />

            <WorkerJobSummary worker={workerDetails}/>

            <WorkerSkills
              workerId={this.initialState.id}
            />

            <WorkerInterview
              workerId={this.initialState.id}
            />

            <section>
              <h4 className='section-header'>Additional Information</h4>
              <Link
                className='button button-primary mr-3 mb-3'
                to={`${url}/references`}
              >References</Link>
              <Link
                className='button button-primary'
                to={`${url}/checkr`}
              >Checkr</Link>
            </section>
          </main>
        )}
        {activeTab === 'jobs' && (
          <WorkerJobsList
            {...this.props}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = state => {
  return {
    workerDetails: state.worker.workerDetails,
    languagesList: state.languagesList
  };
};

const mapDispatchToProps = {
  getDetailedUser,
  clearWorkerDetails,
  editWorkerUser,
  uploadFile,
  makeDecisionAfterCheckr,
  getLanguagesList,
  startSupportConversation,
  UIShowAlert,
  UIShowConfirm,
  getWorkerJobsCompletedAction
};

export default connect(mapStateToProps, mapDispatchToProps)(WorkerDetails);



// WEBPACK FOOTER //
// ./src/components/app/workerDetails/index.js